import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/types/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  FileText, 
  User, 
  Building, 
  Phone, 
  Calendar, 
  ExternalLink, 
  Download,
  Mail,
  MapPin,
  Users,
  Paperclip,
  Eye
} from "lucide-react";
import { useClientStatusOptions } from "@/hooks/useClientStatusOptions";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { useState } from "react";

interface ClientProfilePopoverProps {
  clientId: string;
  children: React.ReactNode;
}

const attachmentTypes = {
  'passport': 'جوازات المسافر',
  'international_flight': 'الطيران الدولي',
  'domestic_flight': 'الطيران الداخلي', 
  'hotel_booking': 'حجوزات الفنادق',
  'transport': 'المواصلات',
  'invoices': 'الفواتير',
  'other': 'أخرى'
};

export const ClientProfilePopover = ({ clientId, children }: ClientProfilePopoverProps) => {
  const [open, setOpen] = useState(false);
  const [fileViewDialog, setFileViewDialog] = useState<{ open: boolean; file: any; url: string }>({ 
    open: false, 
    file: null, 
    url: '' 
  });
  const { getStatusStyle, getStatusLabel } = useClientStatusOptions();
  
  const { data: client, isLoading } = useQuery({
    queryKey: ["client-profile", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select(`
          *,
          companies:company_id (
            id,
            name_ar,
            address_ar,
            mobile
          ),
          creator:created_by (
            id,
            name
          ),
          client_assignments (
            id,
            role,
            employee_id,
            employees (
              id,
              name
            )
          )
        `)
        .eq("id", clientId)
        .single();
      
      if (error) throw error;
      
      return {
        ...data,
        company: data.companies?.name_ar || data.company
      } as Client;
    },
    enabled: open && !!clientId
  });

  const parseAttachmentInfo = (attachment: string) => {
    const parts = attachment.split(':');
    return {
      type: parts[0],
      filename: parts[1],
      viewLink: parts[2],
      downloadLink: parts[3]
    };
  };

  const attachments = client?.attachments?.map(parseAttachmentInfo) || [];

  const handleViewFile = async (attachment: any) => {
    if (attachment.viewLink) {
      setFileViewDialog({ 
        open: true, 
        file: attachment, 
        url: attachment.viewLink 
      });
    }
  };

  const handleDownloadFile = async (attachment: any) => {
    if (attachment.downloadLink) {
      try {
        const response = await fetch(attachment.downloadLink);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename || 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback to opening in new tab
        window.open(attachment.downloadLink, '_blank');
      }
    }
  };

  const getFileType = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  return (
    <>
      <Dialog open={fileViewDialog.open} onOpenChange={(open) => setFileViewDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">
              {attachmentTypes[fileViewDialog.file?.type] || fileViewDialog.file?.type} - {fileViewDialog.file?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {fileViewDialog.file && getFileType(fileViewDialog.file.filename) === 'image' && (
              <div className="flex justify-center items-center h-full max-h-[70vh]">
                <img 
                  src={fileViewDialog.url} 
                  alt={fileViewDialog.file.filename}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            {fileViewDialog.file && getFileType(fileViewDialog.file.filename) === 'pdf' && (
              <div className="h-[70vh] w-full">
                <iframe 
                  src={fileViewDialog.url}
                  className="w-full h-full border-0"
                  title={fileViewDialog.file.filename}
                />
              </div>
            )}
            {fileViewDialog.file && getFileType(fileViewDialog.file.filename) === 'other' && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">{fileViewDialog.file.filename}</p>
                <p className="text-muted-foreground mb-4">لا يمكن معاينة هذا النوع من الملفات</p>
                <Button onClick={() => handleDownloadFile(fileViewDialog.file)}>
                  <Download className="w-4 h-4 ml-2" />
                  تحميل الملف
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        className="w-[500px] p-0 shadow-2xl border-0 bg-gradient-to-br from-background to-muted/20" 
        align="center"
        side="left"
        dir="rtl"
      >
        <ScrollArea className="h-[600px]">
          {isLoading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-muted rounded w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                </div>
              </div>
            </div>
          ) : client ? (
            <div className="space-y-0">
              {/* Header */}
              <div className="bg-gradient-to-l from-primary/10 to-primary/20 p-6 border-b">
                <div className="flex items-start justify-between mb-3" dir="rtl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-right">
                      <h3 className="text-xl font-bold text-foreground">{client.name_ar}</h3>
                      {client.name_en && (
                        <p className="text-sm text-muted-foreground">{client.name_en}</p>
                      )}
                    </div>
                  </div>
                  {client.status && (
                    <Badge 
                      className="text-white shadow-lg mr-3"
                      style={getStatusStyle(client.status)}
                    >
                      {getStatusLabel(client.status)}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground justify-start" dir="rtl">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{format(new Date(client.created_at), "dd MMM yyyy", { locale: ar })}</span>
                  </div>
                  {client.mobile && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{client.mobile}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* معلومات الشركة */}
              {(client.companies || client.company) && (
                <div className="p-6 border-b bg-gradient-to-l from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20" dir="rtl">
                  <div className="flex items-center gap-3 mb-3">
                    <Building className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-foreground">معلومات الشركة</h4>
                  </div>
                  <div className="space-y-2 text-sm text-right">
                    <div>
                      <span className="font-medium text-foreground">
                        {client.companies?.name_ar || client.company}
                      </span>
                    </div>
                    {client.companies?.address_ar && (
                      <div className="flex items-center gap-2 text-muted-foreground justify-start">
                        <MapPin className="w-4 h-4" />
                        <span>{client.companies.address_ar}</span>
                      </div>
                    )}
                    {client.companies?.mobile && (
                      <div className="flex items-center gap-2 text-muted-foreground justify-start">
                        <Phone className="w-4 h-4" />
                        <span>{client.companies.mobile}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* المعلومات الإضافية */}
              {client.additional_info && (
                <div className="p-6 border-b bg-gradient-to-l from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20" dir="rtl">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-foreground">المعلومات الإضافية</h4>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed text-right">
                    {client.additional_info}
                  </p>
                </div>
              )}

              {/* معلومات الجواز */}
              {client.passport_info && (
                <div className="p-6 border-b bg-gradient-to-l from-purple-50/50 to-violet-50/50 dark:from-purple-950/20 dark:to-violet-950/20" dir="rtl">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-foreground">معلومات الجواز</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm text-right">
                    {Object.entries(client.passport_info as Record<string, any>).map(([key, value]) => (
                      value && (
                        <div key={key} className="space-y-1">
                          <span className="text-muted-foreground font-medium">
                            {key === 'passportNumber' ? 'رقم الجواز' :
                             key === 'issueDate' ? 'تاريخ الإصدار' :
                             key === 'expiryDate' ? 'تاريخ الانتهاء' :
                             key === 'nationality' ? 'الجنسية' : key}:
                          </span>
                          <p className="text-foreground font-medium">{value}</p>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* المرفقات */}
              {attachments.length > 0 && (
                <div className="p-6 border-b bg-gradient-to-l from-orange-50/50 to-amber-50/50 dark:from-orange-950/20 dark:to-amber-950/20" dir="rtl">
                  <div className="flex items-center gap-3 mb-3">
                    <Paperclip className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-foreground">المرفقات ({attachments.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {attachments.slice(0, 3).map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-background/60 rounded-lg border" dir="rtl">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-primary shrink-0" />
                          <div className="min-w-0 flex-1 text-right">
                            <p className="text-sm font-medium text-foreground truncate">
                              {attachmentTypes[attachment.type] || attachment.type}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {attachment.filename}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-1 shrink-0">
                          {attachment.viewLink && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewFile(attachment)}
                              className="h-8 w-8 p-0"
                              title="عرض"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          )}
                          {attachment.downloadLink && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadFile(attachment)}
                              className="h-8 w-8 p-0"
                              title="تحميل"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {attachments.length > 3 && (
                      <p className="text-xs text-muted-foreground text-center pt-2">
                        وملفات أخرى... ({attachments.length - 3})
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* الموظفون المكلفون */}
              {client.client_assignments && client.client_assignments.length > 0 && (
                <div className="p-6 border-b bg-gradient-to-l from-teal-50/50 to-cyan-50/50 dark:from-teal-950/20 dark:to-cyan-950/20" dir="rtl">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-5 h-5 text-teal-600" />
                    <h4 className="font-semibold text-foreground">الموظفون المكلفون</h4>
                  </div>
                  <div className="space-y-2">
                    {client.client_assignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center gap-3 p-2 bg-background/60 rounded-lg" dir="rtl">
                        <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0 text-right">
                          <p className="text-sm font-medium text-foreground truncate">
                            {assignment.employees?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {assignment.role}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* من أنشأ العميل */}
              {client.creator && 'name' in client.creator && (
                <div className="p-6 bg-gradient-to-l from-slate-50/50 to-gray-50/50 dark:from-slate-950/20 dark:to-gray-950/20" dir="rtl">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground justify-start">
                    <User className="w-4 h-4" />
                    <span>أنشئ بواسطة: <span className="font-medium text-foreground">{client.creator.name}</span></span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">لم يتم العثور على بيانات العميل</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
    </>
  );
};